/**
 * The fetch wrapper. The only place the frontend talks HTTP.
 *
 * Handles: auth headers, the error envelope, request ids, timeouts, and idempotency.
 * Reference: docs/09-api/01-api-conventions.md
 */

import { ApiError, NetworkError } from './errors'

const BASE = import.meta.env.VITE_API_URL ?? ''
const DEFAULT_TIMEOUT_MS = 30_000

type Query = Record<string, string | number | boolean | null | undefined>

interface RequestOptions {
  query?: Query
  signal?: AbortSignal
  timeoutMs?: number
  idempotencyKey?: string
  ifMatch?: string
  headers?: Record<string, string>
}

/** Injected by the auth layer (P2) so this module has no dependency on it. */
let getAccessToken: () => string | null = () => null

export function setTokenProvider(fn: () => string | null): void {
  getAccessToken = fn
}

function buildUrl(path: string, query?: Query): string {
  const url = new URL(path, BASE || window.location.origin)
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
  }
  return url.toString()
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const { query, signal, timeoutMs = DEFAULT_TIMEOUT_MS, idempotencyKey, ifMatch } = options

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': crypto.randomUUID().replace(/-/g, ''),
    ...options.headers,
  }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey
  if (ifMatch) headers['If-Match'] = ifMatch

  // Combine the caller's signal with our own timeout.
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  signal?.addEventListener('abort', () => controller.abort(), { once: true })

  const init: RequestInit = {
    method,
    headers,
    signal: controller.signal,
    credentials: 'omit',
  }
  // Assigning `body: undefined` is not the same as omitting it under
  // exactOptionalPropertyTypes, and a GET with a body key is invalid.
  if (body !== undefined) init.body = JSON.stringify(body)

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), init)
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw signal?.aborted ? err : new NetworkError('The request timed out.')
    }
    throw new NetworkError()
  } finally {
    clearTimeout(timeout)
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const payload: unknown = text ? JSON.parse(text) : null

  if (!response.ok) throw ApiError.fromResponse(response.status, payload)
  return payload as T
}

/** GET a file (a PDF, say) with the same auth and error handling as JSON calls. */
async function download(path: string, options: RequestOptions = {}): Promise<Blob> {
  const headers: Record<string, string> = { 'X-Request-Id': crypto.randomUUID().replace(/-/g, '') }
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  let response: Response
  try {
    response = await fetch(buildUrl(path, options.query), {
      method: 'GET',
      headers,
      credentials: 'omit',
      signal: AbortSignal.timeout(options.timeoutMs ?? DEFAULT_TIMEOUT_MS),
    })
  } catch {
    throw new NetworkError()
  }
  if (!response.ok) {
    const text = await response.text()
    let payload: unknown = null
    try {
      payload = text ? JSON.parse(text) : null
    } catch {
      // Not JSON (a proxy error page): ApiError copes with a null payload.
    }
    throw ApiError.fromResponse(response.status, payload)
  }
  return response.blob()
}

/** POST a file as the raw request body (its name goes in ?name=); the reply is JSON. */
async function upload<T>(path: string, file: Blob, filename: string): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': file.type || 'application/octet-stream',
    'X-Request-Id': crypto.randomUUID().replace(/-/g, ''),
  }
  const token = getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  let response: Response
  try {
    response = await fetch(buildUrl(path, { name: filename }), {
      method: 'POST',
      headers,
      body: file,
      credentials: 'omit',
      signal: AbortSignal.timeout(60_000),
    })
  } catch {
    throw new NetworkError()
  }
  const text = await response.text()
  let payload: unknown = null
  try {
    payload = text ? JSON.parse(text) : null
  } catch {
    // Not JSON (a proxy error page): ApiError copes with a null payload.
  }
  if (!response.ok) throw ApiError.fromResponse(response.status, payload)
  return payload as T
}

export const api = {
  download,
  upload,
  get: <T>(path: string, query?: Query, options?: RequestOptions) =>
    request<T>('GET', path, undefined, { ...options, ...(query ? { query } : {}) }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, body, options),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, body, options),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, body, options),
  delete: <T>(path: string, options?: RequestOptions) =>
    request<T>('DELETE', path, undefined, options),
}
